import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';

interface PointerModeToggleProps {
  readonly tool: EditorTool;
  readonly setTool: (tool: EditorTool) => void;
  readonly hasSelectableContent: boolean;
  readonly children?: ReactNode;
  readonly selectHidden?: boolean;
  readonly panHidden?: boolean;
  readonly onSelectAll: () => void;
  // `compact` stacks the two segments — for the collapsed rail strip.
  readonly compact?: boolean;
  readonly hideUnavailable?: boolean;
}

// Draw, Select, Select all and Pan on the toolbar, exactly one mode pressed (#1071: Draw was only in More).
export function PointerModeToggle({
  tool,
  setTool,
  hasSelectableContent,
  onSelectAll,
  children,
  selectHidden = false,
  panHidden = false,
  compact = false,
  hideUnavailable = false
}: PointerModeToggleProps) {
  const { t } = useTranslation();
  const drawActive = tool === 'draw';
  const selectActive = tool === 'select';
  const panActive = tool === 'pan';

  return (
    <div
      className={`pointer-mode-toggle ${compact ? 'pointer-mode-toggle--compact' : ''}`}
      role="group"
      aria-label={t('drawingToolNav.pointerModes')}
    >
      <span className="viewport-group" data-toolbar-group="modes" hidden={selectHidden}>
        <button
          type="button"
          className={drawActive ? 'active' : ''}
          aria-pressed={drawActive}
          aria-label={t('drawingToolNav.draw')}
          title={t('drawingToolNav.draw')}
          onClick={() => setTool('draw')}
        >
          <Icon name="edit" />
        </button>
        {!hideUnavailable || hasSelectableContent ? (
          <>
            <button
              type="button"
              className={selectActive ? 'active' : ''}
              aria-pressed={selectActive}
              aria-label={t('drawingToolNav.selectAndEdit')}
              title={t('drawingToolNav.selectAndEdit')}
              disabled={!hasSelectableContent}
              onClick={() => setTool('select')}
            >
              <Icon name="arrowSelectorTool" />
            </button>
            <button
              type="button"
              aria-label={t('editorToolPalette.selection.selectAll')}
              title={t('editorToolPalette.selection.selectAll')}
              disabled={!hasSelectableContent}
              onClick={onSelectAll}
            >
              <Icon name="selectAll" />
            </button>
          </>
        ) : null}
      </span>
      {children}
      <span className="viewport-group" data-toolbar-group="pan" hidden={panHidden}>
        {hasSelectableContent ? <span className="viewport-divider" aria-hidden="true" /> : null}
        <button
          type="button"
          className={panActive ? 'active' : ''}
          aria-pressed={panActive}
          aria-label={t('paperViewportControls.pan')}
          title={t('paperViewportControls.pan')}
          onClick={() => setTool('pan')}
        >
          <Icon name="panTool" />
        </button>
      </span>
    </div>
  );
}
